import queryString from 'query-string';
import { z } from 'zod';

import { contestsServiceV2Instance } from '~/domains/contest/domains/services/contest.service.instance';

export const ContestEntitySourceV2Schema = z.enum(['splash_v2', 'ryp', 'ofp', 'splash']);
export type ContestEntitySourceV2 = z.infer<typeof ContestEntitySourceV2Schema>;

export const SPLASH_CONTEST_SOURCES_V2 = [
  ContestEntitySourceV2Schema.enum.splash,
  ContestEntitySourceV2Schema.enum.splash_v2,
].join(',');

export const PublicUserV2Schema = z.object({
  id: z.string(),
  handle: z.string(),
  avatarUrl: z.string().nullish(),
});
export type PublicUserV2 = z.infer<typeof PublicUserV2Schema>;

export const PublicLeagueV2Schema = z.object({
  id: z.string(),
  name: z.string(),
  alias: z.string(),
  sport: z.string(),
  icon: z.string().nullish(),
});
export type PublicLeagueV2 = z.infer<typeof PublicLeagueV2Schema>;

export const ContestUrlsV2Schema = z.object({
  gameCenterUrl: z.string().nullish(),
  manageUrl: z.string().nullish(),
  restartUrl: z.string().nullish(),
  shareUrl: z.string().nullish(),
});
export type ContestUrlsV2 = z.infer<typeof ContestUrlsV2Schema>;

export const ContestEntriesV2Schema = z.object({
  max: z.number(),
  filled: z.number(),
  user: z.number(),
  userAnyStake: z.number().optional(),
  maxPerUser: z.number(),
  isUnlimitedEntries: z.boolean(),
});
export type ContestEntriesV2 = z.infer<typeof ContestEntriesV2Schema>;

export const ContestAlertV2Schema = z.object({
  type: z.string(),
  status: z.string(),
  message: z.string(),
});
export type ContestAlertV2 = z.infer<typeof ContestAlertV2Schema>;

export const ContestAlertsV2Schema = z.object({
  values: z.array(ContestAlertV2Schema),
});
export type ContestAlertsV2 = z.infer<typeof ContestAlertsV2Schema>;

const ContestV2CoreSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  entitySource: ContestEntitySourceV2Schema,
  isEntertainmentOnly: z.boolean().nullish(),
  isStarted: z.boolean(),
  leagues: z.array(PublicLeagueV2Schema),
  contestType: z.string(),
  contestTypeAltText: z.string().nullish(),
  startDate: z.string(),
  endDate: z.string().nullish(),
  settledDate: z.string().nullish(),
  entryFeeCents: z.number(),
  entryFeeDollars: z.number(),
  prizePoolCents: z.number(),
  prizePoolDollars: z.number(),
});

export const ContestV2ItemBaseSchema = z.object({
  contest: ContestV2CoreSchema,
  commissioner: PublicUserV2Schema,
  urls: ContestUrlsV2Schema,
  entries: ContestEntriesV2Schema,
});
export type ContestV2ItemBase = z.infer<typeof ContestV2ItemBaseSchema>;

export const CommissionerContestV2ItemSchema = ContestV2ItemBaseSchema.extend({
  alerts: ContestAlertsV2Schema,
});
export type CommissionerContestV2Item = z.infer<typeof CommissionerContestV2ItemSchema>;

export const RankedLeaderboardV2Schema = z.object({
  type: z.literal('ranked_leaderboard'),
  positions: z.array(z.object({ entryId: z.string(), rank: z.number() })),
  totalSpots: z.number(),
  payoutSpots: z.number(),
  userPayoutCents: z.number().nullish(),
  userPayoutDollars: z.number().nullish(),
  userSegmentedPayoutsCents: z.number().nullish(),
  userSegmentedPayoutsDollars: z.number().nullish(),
  isUserEliminated: z.boolean().nullish(),
});
export type RankedLeaderboardV2 = z.infer<typeof RankedLeaderboardV2Schema>;

export const SurvivorLeaderboardV2Schema = z.object({
  type: z.literal('survivor_leaderboard'),
  userAliveEntries: z.number(),
  userTotalEntries: z.number(),
  contestAliveEntries: z.number(),
  contestTotalEntries: z.number(),
  userPayoutCents: z.number().nullish(),
  userPayoutDollars: z.number().nullish(),
  userSegmentedPayoutsCents: z.number().nullish(),
  userSegmentedPayoutsDollars: z.number().nullish(),
  isUserEliminated: z.boolean().nullish(),
});
export type SurvivorLeaderboardV2 = z.infer<typeof SurvivorLeaderboardV2Schema>;

export const MyContestLeaderboardV2Schema = z.discriminatedUnion('type', [
  RankedLeaderboardV2Schema,
  SurvivorLeaderboardV2Schema,
]);
export type MyContestLeaderboardV2 = z.infer<typeof MyContestLeaderboardV2Schema>;

export const MyContestV2ItemSchema = ContestV2ItemBaseSchema.extend({
  leaderboard: MyContestLeaderboardV2Schema.nullish(),
  alerts: ContestAlertsV2Schema,
  nextPickLockDate: z.string().nullish(),
});
export type MyContestV2Item = z.infer<typeof MyContestV2ItemSchema>;

export type GetCommissionerContestsV2Params = {
  tab: 'active' | 'final' | 'previous';
  source?: 'splash' | 'splash_v2' | 'ryp' | 'ofp';
  excludeContestId?: string;
  search?: string;
  status?: 'not_yet_started' | 'started';
  league?: string;
  contestType?: string;
  needsRestart?: 'true';
  resetDismissed?: 'true' | 'false';
  sort?: 'start_date' | 'settled_date' | 'name' | 'entry_fee' | 'total_prizes' | 'entry_count';
  order?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
};

const normalizeV2SourceParam = <TParams extends { source?: string }>(params: TParams): TParams => ({
  ...params,
  ...(params.source ? { source: params.source.toLowerCase() } : {}),
});

export const GetCommissionerContestsV2ResponseSchema = z.object({
  data: z.array(CommissionerContestV2ItemSchema),
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
});
export type GetCommissionerContestsV2Response = z.infer<
  typeof GetCommissionerContestsV2ResponseSchema
>;

export const getCommissionerContestsV2 = async (
  params: GetCommissionerContestsV2Params
): Promise<GetCommissionerContestsV2Response> => {
  const query = queryString.stringify(params);
  const response = await contestsServiceV2Instance.get<GetCommissionerContestsV2Response>(
    `/commissioner/contests?${query}`
  );
  return response.data;
};

const NativeCommissionerContestV2ItemSchema = CommissionerContestV2ItemSchema.extend({
  contest: ContestV2CoreSchema.extend({
    startDate: z.string().datetime({ offset: true }),
    v2Id: z.string().max(128),
  }),
});

export const GetNativeCommissionerContestsV2ResponseSchema = z.object({
  data: z.array(NativeCommissionerContestV2ItemSchema),
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
});
export type GetNativeCommissionerContestsV2Response = z.infer<
  typeof GetNativeCommissionerContestsV2ResponseSchema
>;

export type GetNativeCommissionerContestsV2Params = Omit<GetCommissionerContestsV2Params, 'tab'> & {
  tab: 'previous';
  source: 'ryp' | 'ofp';
  excludeContestId: string;
};

export const getNativeCommissionerContestsV2 = async (
  params: GetNativeCommissionerContestsV2Params
): Promise<GetNativeCommissionerContestsV2Response> => {
  const query = queryString.stringify(params);
  const response = await contestsServiceV2Instance.get<unknown>(`/commissioner/contests?${query}`);
  return GetNativeCommissionerContestsV2ResponseSchema.parse(response.data);
};

export const dismissContestRestartV2 = async (contestId: string) => {
  const response = await contestsServiceV2Instance.patch(
    `/commissioner/contests/${encodeURIComponent(contestId)}`,
    { resetDismissed: false }
  );
  return response.data;
};

export const GetHasPaidContestsV2ResponseSchema = z.object({
  data: z.object({ hasPaidContests: z.boolean() }),
});
export type GetHasPaidContestsV2Response = z.infer<typeof GetHasPaidContestsV2ResponseSchema>;

export const getHasPaidContestsV2 = async (): Promise<GetHasPaidContestsV2Response> => {
  const response = await contestsServiceV2Instance.get<GetHasPaidContestsV2Response>(
    `/commissioner/contests/has-paid-contests`
  );
  return response.data;
};

export type GetCommissionerContestsProfileV2Params = {
  userId?: string;
  handle?: string;
  tab?: 'created' | 'entered';
  status?: string;
  source?: string;
  league?: string;
  beforeEntryDeadline?: 'true';
  offset?: number;
  limit?: number;
};

export const GetCommissionerContestsProfileV2ResponseSchema = z.object({
  data: z.array(ContestV2ItemBaseSchema),
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
});
export type GetCommissionerContestsProfileV2Response = z.infer<
  typeof GetCommissionerContestsProfileV2ResponseSchema
>;

export const getCommissionerContestsProfileV2 = async (
  params: GetCommissionerContestsProfileV2Params
): Promise<GetCommissionerContestsProfileV2Response> => {
  const query = queryString.stringify(normalizeV2SourceParam(params));
  const response = await contestsServiceV2Instance.get<GetCommissionerContestsProfileV2Response>(
    `/commissioner/contests/profile?${query}`
  );
  return response.data;
};

export type GetMyContestsV2Params = {
  status: 'active' | 'finished' | 'finalized' | 'action_required';
  contestType?: string;
  hasEntryFee?: 'true' | 'false';
  keepEliminatedActive?: 'true' | 'false';
  leagues?: string;
  source?: string;
  sortOption?: 'slate_start' | 'end_date' | 'entered_date' | 'entry_fee' | 'prize_pool';
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

export const GetMyContestsV2ResponseSchema = z.object({
  data: z.array(MyContestV2ItemSchema),
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
});
export type GetMyContestsV2Response = z.infer<typeof GetMyContestsV2ResponseSchema>;

export const getMyContestsV2 = async (
  params: GetMyContestsV2Params
): Promise<GetMyContestsV2Response> => {
  const query = queryString.stringify(normalizeV2SourceParam(params));
  const response = await contestsServiceV2Instance.get<GetMyContestsV2Response>(
    `/my-contests?${query}`
  );
  return response.data;
};

export const GetContestUsersMetaV2ResponseSchema = z.object({
  totals: z.object({
    contests: z.number(),
    entries: z.number(),
  }),
});
export type GetContestUsersMetaV2Response = z.infer<typeof GetContestUsersMetaV2ResponseSchema>;

const ContestUserV2Schema = z
  .object({
    id: z.string(),
    user: z
      .object({
        handle: z.string().nullish(),
        avatarUrl: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
      })
      .passthrough(),
  })
  .passthrough();

export const GetContestUsersV2ResponseSchema = z
  .object({
    data: z.array(ContestUserV2Schema),
    offset: z.number(),
    limit: z.number(),
    total: z.number(),
  })
  .passthrough();
export type GetContestUsersV2Response = z.infer<typeof GetContestUsersV2ResponseSchema>;

export const getContestUsersV2 = async (
  contestId: string,
  params: { offset: number; limit: number }
): Promise<GetContestUsersV2Response> => {
  const query = queryString.stringify(params);
  const response = await contestsServiceV2Instance.get<unknown>(
    `/contests/${encodeURIComponent(contestId)}/contest-users?${query}`
  );
  return GetContestUsersV2ResponseSchema.parse(response.data);
};

export const getContestUsersMetaV2 = async (): Promise<GetContestUsersMetaV2Response> => {
  const response =
    await contestsServiceV2Instance.get<GetContestUsersMetaV2Response>(`/contest-users/meta`);
  return response.data;
};

// Use gameCenterUrl (the resolved route); shareUrl is the /contest/{id} deep link
// itself and would loop back here.
export const GetContestV2GameCenterUrlResponseSchema = z.object({
  data: z.object({
    urls: z.object({ gameCenterUrl: z.string().nullish() }),
  }),
});

// Runs in getServerSideProps on a hot path — fail fast instead of tying up SSR.
const CONTEST_V2_LOOKUP_TIMEOUT_MS = 3000;

export const getContestV2GameCenterUrl = async (contestId: string): Promise<string | null> => {
  const response = await contestsServiceV2Instance.get<unknown>(
    `/contests/${encodeURIComponent(contestId)}`,
    { timeout: CONTEST_V2_LOOKUP_TIMEOUT_MS }
  );
  return (
    GetContestV2GameCenterUrlResponseSchema.parse(response.data).data.urls.gameCenterUrl ?? null
  );
};
